// État de la sidebar.
//
// La sidebar est persistante : à chaque navigation Inertia (événement
// `navigate`, qui ne part pas pour les rechargements partiels ni les visites
// sur la même URL), classes du <body> remises à zéro, état replié relu dans
// localStorage, sous-menus repris de l'état serveur.
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { router } from '@inertiajs/react'

const STORAGE_KEY = 'sidebar-collapsed'
// Jusqu'à 1024 px inclus, mise en page mobile (lib/webview.ts, barre du bas) :
// sidebar masquée (sidebar.scss).
const MOBILE_MAX = 1024

// useLayoutEffect côté client seulement (le rendu serveur n'a pas d'effets).
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

function storedCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * `defaultOpen` : sous-menu ouvert d'après le serveur (classe « active
 * open »), reprise à chaque navigation.
 */
export function useSidebar(defaultOpen: string | null) {
  const [collapsed, setCollapsed] = useState(false)
  // null : pas de choix de l'utilisateur depuis la dernière navigation.
  const [openOverride, setOpenOverride] = useState<{ key: string | null } | null>(null)
  const openKey = openOverride ? openOverride.key : defaultOpen

  const applyCollapsed = useCallback((value: boolean) => {
    setCollapsed(value)
    document.body.classList.toggle('sidebar-collapsed', value)
  }, [])

  // État replié enregistré (jamais en mise en page mobile), puis has-sidebar.
  const restore = useCallback(() => {
    if (storedCollapsed()) applyCollapsed(true)
    document.body.classList.add('has-sidebar')
    if (window.innerWidth <= MOBILE_MAX) applyCollapsed(false)
  }, [applyCollapsed])

  useIsoLayoutEffect(() => {
    restore()
    // checkScreenSize : jusqu'à 1024 px la sidebar n'est jamais repliée ; elle ne
    // se replie pas non plus en repassant au-dessus (seulement à la visite suivante).
    const onResize = () => {
      if (window.innerWidth <= MOBILE_MAX) applyCollapsed(false)
    }
    window.addEventListener('resize', onResize)
    const offNavigate = router.on('navigate', (event) => {
      const body = document.body
      if (event.detail.type === 'initial') {
        // Au chargement, les classes de la sidebar passent après webview-mode
        // dans body.className.
        for (const cls of ['sidebar-collapsed', 'has-sidebar']) {
          if (body.classList.contains(cls)) {
            body.classList.remove(cls)
            body.classList.add(cls)
          }
        }
        return
      }
      body.classList.remove('sidebar-mobile-open')
      // sidebar-collapsed n'est remis que si l'état enregistré le demande.
      applyCollapsed(false)
      restore()
      setOpenOverride(null)
    })
    return () => {
      offNavigate()
      window.removeEventListener('resize', onResize)
      document.body.classList.remove('has-sidebar', 'sidebar-collapsed', 'sidebar-mobile-open')
    }
  }, [restore, applyCollapsed])

  // toggle() : aucun bouton ne l'appelle pour l'instant ; gardé pour pouvoir
  // en remettre un.
  const toggle = useCallback(() => {
    const next = !collapsed
    applyCollapsed(next)
    try {
      localStorage.setItem(STORAGE_KEY, String(next))
    } catch { /* ignore */ }
    if (next) setOpenOverride({ key: null })
  }, [collapsed, applyCollapsed])

  // Clic sur le lien principal d'un sous-menu : il navigue ET ouvre son sous-menu.
  const openWithNavigation = useCallback(
    (key: string) => {
      if (!collapsed) setOpenOverride({ key })
    },
    [collapsed],
  )

  // Clic sur la flèche : ouvre ou ferme le sous-menu, sans naviguer.
  const toggleSubmenu = useCallback(
    (key: string) => {
      if (collapsed) return
      setOpenOverride({ key: openKey === key ? null : key })
    },
    [collapsed, openKey],
  )

  const closeMobile = useCallback(() => document.body.classList.remove('sidebar-mobile-open'), [])
  const openMobile = useCallback(() => document.body.classList.add('sidebar-mobile-open'), [])

  return { collapsed, openKey, toggle, openWithNavigation, toggleSubmenu, openMobile, closeMobile }
}
